import React from 'react';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-lg">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: August 05, 2025</p>

          <p className="mb-6">
            This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
          </p>
          <p className="mb-8">
            We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Interpretation and Definitions
          </h2>
          
          <h3 className="text-2xl font-semibold mb-4 text-gray-900">Interpretation</h3>
          <p className="mb-6">
            The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
          </p>

          <h3 className="text-2xl font-semibold mb-4 text-gray-900">Definitions</h3>
          <p className="mb-4">For the purposes of this Privacy Policy:</p>
          <ul className="list-disc list-inside space-y-4 mb-8">
            <li>
              <strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.
            </li>
            <li>
              <strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to IGReview, your trusted platform for Instagram service reviews.
            </li>
            <li>
              <strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.
            </li>
            <li>
              <strong>Country</strong> refers to: Australia
            </li>
            <li>
              <strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.
            </li>
            <li>
              <strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.
            </li>
            <li>
              <strong>Service</strong> refers to the Website.
            </li>
            <li>
              <strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).
            </li>
            <li>
              <strong>Website</strong> refers to IGReview, accessible from localhost:3000
            </li>
            <li>
              <strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.
            </li>
          </ul>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Collecting and Using Your Personal Data
          </h2>
          <h3 className="text-2xl font-semibold mb-4 text-gray-900">Types of Data Collected</h3>
          
          <h4 className="text-xl font-semibold mb-3 text-gray-900">Personal Data</h4>
          <p className="mb-4">
            While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Email address</li>
            <li>First name and last name</li>
            <li>Instagram username</li>
            <li>Usage Data</li>
          </ul>

          <h4 className="text-xl font-semibold mb-3 text-gray-900">
            Information from Third-Party Social Media Services
          </h4>
          <p className="mb-4">
            The Company allows You to create an account and log in to use the Service through the following Third-party Social Media Services:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Instagram</li>
          </ul>
          <p className="mb-4">
            If You decide to register through or otherwise grant us access to a Third-Party Social Media Service, We may collect Personal data that is already associated with Your Third-Party Social Media Service's account, such as Your name, Your email address, Your activities or Your contact list associated with that account.
          </p>

          <h4 className="text-xl font-semibold mb-3 text-gray-900">Usage Data</h4>
          <p className="mb-6">
            Usage Data is collected automatically when using the Service. Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Use of Your Personal Data
          </h2>
          <p className="mb-4">The Company may use Personal Data for the following purposes:</p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>
              <strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.
            </li>
            <li>
              <strong>To manage Your Account:</strong> to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.
            </li>
            <li>
              <strong>To contact You:</strong> To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.
            </li>
            <li>
              <strong>For review verification:</strong> To verify the authenticity of reviews and ensure service quality by validating Instagram profiles and service transactions.
            </li>
            <li>
              <strong>To improve our Service:</strong> To analyze usage patterns and improve the functionality and user experience of our review platform.
            </li>
          </ul>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Security of Your Personal Data
          </h2>
          <p className="mb-6">
            The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Review Data and Verification
          </h2>
          <p className="mb-6">
            When you submit a review through our Service, we collect and store review data including ratings, comments, service types, and verification codes. This information is used to maintain the integrity of our review system and provide valuable insights to other users. All reviews are subject to moderation and verification processes.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Children's Privacy
          </h2>
          <p className="mb-6">
            Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Links to Other Websites
          </h2>
          <p className="mb-6">
            Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Changes to this Privacy Policy
          </h2>
          <p className="mb-6">
            We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page. We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>

          <h2 className="text-3xl font-bold border-t border-gray-200 pt-8 mb-6 text-gray-900">
            Contact Us
          </h2>
          <p className="mb-4">If you have any questions about this Privacy Policy, You can contact us:</p>
          <ul className="list-disc list-inside space-y-2 mb-8">
            <li>By email: privacy@igreview.com</li>
            <li>Through our contact form on this website</li>
            <li>Via Instagram: @theluxstone</li>
          </ul>

          <div className="mt-8 p-6 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-lg font-semibold text-purple-800 mb-2">
              🛡️ Your Privacy Matters
            </h3>
            <p className="text-purple-700">
              We are committed to protecting your privacy and being transparent about how we collect and use your data. If you have any concerns or questions about our privacy practices, please don't hesitate to contact us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
